import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { GroupList, GroupCardGrid, GroupCreateDialog } from '@authifi/clique-react-ui';
import type { Group } from '@authifi/clique-react-ui';
import { EmailTemplateSelector, INVITATION_EMAIL_CATEGORY } from '@authifi/email-templates';

type ViewMode = 'list' | 'grid';

const tabs: { key: ViewMode; label: string; icon: string }[] = [
  { key: 'list', label: 'List', icon: 'pi pi-list' },
  { key: 'grid', label: 'Grid', icon: 'pi pi-th-large' },
];

export function GroupsPage() {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  const handleGroupSelect = useCallback(
    (group: Group) => {
      navigate(`/groups/${group.id}`);
    },
    [navigate],
  );

  const handleGroupCreated = useCallback((group: Group) => {
    toast.current?.show({
      severity: 'success',
      summary: 'Group Created',
      detail: `"${group.name}" has been created.`,
      life: 3000,
    });
  }, []);

  const handleSuccess = useCallback((message: string) => {
    toast.current?.show({ severity: 'success', summary: 'Success', detail: message, life: 3000 });
  }, []);

  const handleError = useCallback((message: string) => {
    toast.current?.show({ severity: 'error', summary: 'Error', detail: message, life: 5000 });
  }, []);

  return (
    <div>
      <Toast ref={toast} />
      <h2 className="text-2xl font-semibold text-[var(--clique-fg)] mb-2">Groups</h2>
      <p className="text-[var(--clique-fg-muted)] mb-6">Manage groups and group memberships.</p>

      <div className="flex gap-1 border-b border-[var(--clique-border)] mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setViewMode(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium cursor-pointer transition-colors border-b-2 -mb-px ${
              viewMode === tab.key
                ? 'border-[var(--clique-primary)] text-[var(--clique-primary)]'
                : 'border-transparent text-[var(--clique-fg-muted)] hover:text-[var(--clique-fg)] hover:border-[var(--clique-border)]'
            }`}
          >
            <i aria-hidden="true" className={tab.icon} />
            {tab.label}
          </button>
        ))}
      </div>

      {viewMode === 'list' && (
        <GroupList
          onGroupSelect={handleGroupSelect}
          onCreateGroup={() => setShowCreateDialog(true)}
        />
      )}

      {viewMode === 'grid' && (
        <GroupCardGrid
          useDialog
          onCreateGroup={() => setShowCreateDialog(true)}
          onSuccess={handleSuccess}
          onError={handleError}
          renderInviteTemplateSelector={({ value, onChange, disabled }) => (
            <EmailTemplateSelector
              value={value}
              onChange={onChange}
              disabled={disabled}
              categoryLabel={INVITATION_EMAIL_CATEGORY}
            />
          )}
        />
      )}

      <GroupCreateDialog
        visible={showCreateDialog}
        onHide={() => setShowCreateDialog(false)}
        onCreated={handleGroupCreated}
      />
    </div>
  );
}
